import type { GencowAuthConfig } from "@gencow/core";
import { isOriginAllowed } from "./server-origin-policy.js";

type OAuthCallbackResolverOptions = {
  requestedCallbackURL?: string;
  authConfig?: GencowAuthConfig;
  baseURL: string;
  appPublicDomain?: string;
  trustedOrigins: string[];
};

function normalizeConfiguredDomain(value?: string): string | null {
  const raw = value?.trim();
  if (!raw) return null;
  return (
    raw
      .replace(/^https?:\/\//, "")
      .split("/")[0]
      ?.trim() || null
  );
}

function addDomainOrigins(origins: Set<string>, value?: string) {
  const domain = normalizeConfiguredDomain(value);
  if (!domain) return;

  origins.add(`https://${domain}`);
  origins.add(`http://${domain}`);

  if (domain.startsWith("www.") || domain.includes("*") || domain.includes(":")) {
    return;
  }

  origins.add(`https://www.${domain}`);
  origins.add(`http://www.${domain}`);
}

function addConfiguredOrigin(origins: Set<string>, value?: string) {
  const raw = value?.trim();
  if (!raw) return;

  try {
    origins.add(new URL(raw).origin);
  } catch {
    addDomainOrigins(origins, raw);
  }
}

function buildOAuthCallbackURLFromOrigin(origin: string): string {
  const url = new URL(origin);
  url.pathname = "/auth/callback";
  url.search = "";
  url.hash = "";
  return url.toString();
}

export function resolveOAuthTrustedOrigins(
  baseTrustedOrigins: string[],
  authConfig?: GencowAuthConfig,
): string[] {
  const origins = new Set(baseTrustedOrigins);

  addConfiguredOrigin(origins, authConfig?.oauth?.callbackURL);
  for (const callbackURL of authConfig?.oauth?.allowedCallbackURLs ?? []) {
    addConfiguredOrigin(origins, callbackURL);
  }

  return [...origins];
}

export function resolveOAuthCallbackURL(options: OAuthCallbackResolverOptions): string | null {
  const trustedOrigins = resolveOAuthTrustedOrigins(options.trustedOrigins, options.authConfig);
  const base = options.baseURL;
  const configured = options.authConfig?.oauth?.callbackURL?.trim();
  const appPublicDomain = normalizeConfiguredDomain(options.appPublicDomain);
  const candidate =
    options.requestedCallbackURL?.trim() ||
    (configured ? new URL(configured, base).toString() : null) ||
    (appPublicDomain ? buildOAuthCallbackURLFromOrigin(`https://${appPublicDomain}`) : null) ||
    buildOAuthCallbackURLFromOrigin(new URL(base).origin);

  try {
    const url = new URL(candidate, base);
    if (!["http:", "https:"].includes(url.protocol)) return null;
    return isOriginAllowed(url.origin, trustedOrigins) ? url.toString() : null;
  } catch {
    return null;
  }
}

export function isTrustedOAuthCallbackURL(value: string, options: OAuthCallbackResolverOptions): boolean {
  return resolveOAuthCallbackURL({ ...options, requestedCallbackURL: value }) !== null;
}
